import type { TableFilter } from '../../hooks/use-filter-state';
import type { DataTableGroupFieldConfig, DataTableGroupSortDirection } from '../../hooks/use-data-table-state';
import { UNASSIGNED_GROUP_KEY } from './data-table-utils';

/**
 * Determines which groups to display when a filter overlaps with the active
 * group-by field.
 *
 * Example: grouped by "teams" (filterKey="teamIds") and the user filters
 * teamIds to ["team-a-id"] → only the "Team A" group is visible.
 *
 * Returns the full group list when there's no overlap or no active filter.
 *
 * When `sortDirection` is provided, the returned groups are sorted by label.
 * The unassigned group (`__unassigned__`) is always placed at the end for
 * ascending and the beginning for descending.
 */
export function resolveVisibleGroups(
  groupConfig: DataTableGroupFieldConfig,
  filters: TableFilter[],
  sortDirection?: DataTableGroupSortDirection,
): Array<{ key: string; label: string }> {
  const allGroups = groupConfig.groups ?? [];
  if (allGroups.length === 0) return allGroups;

  const { filterKey } = groupConfig;

  let result: Array<{ key: string; label: string }>;

  if (!filterKey) {
    result = [...allGroups];
  } else {
    const matchingFilter = filters.find((f) => f.key === filterKey);
    if (!matchingFilter || !matchingFilter.isActive || matchingFilter.values.length === 0) {
      result = [...allGroups];
    } else {
      const allowedKeys = new Set(matchingFilter.values);
      result = allGroups.filter((g) => allowedKeys.has(g.key));
    }
  }

  if (sortDirection) {
    // Pull out unassigned group before sorting
    const unassignedIdx = result.findIndex((g) => g.key === UNASSIGNED_GROUP_KEY);
    const unassigned = unassignedIdx !== -1 ? result.splice(unassignedIdx, 1)[0] : null;

    if (groupConfig.preserveOrder) {
      if (sortDirection === 'desc') result.reverse();
    } else {
      const direction = sortDirection === 'asc' ? 1 : -1;
      result.sort((a, b) => direction * a.label.localeCompare(b.label));
    }

    // Place unassigned at end (asc) or beginning (desc)
    if (unassigned) {
      if (sortDirection === 'asc') {
        result.push(unassigned);
      } else {
        result.unshift(unassigned);
      }
    }
  }

  return result;
}
